import { useRef, useEffect, useState } from "react";
import { Layout } from "@/components/layout";
import { AIChat } from "@/components/ui/ai-chat";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { MessageSquare, LightbulbIcon, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";

// Sample suggested prompts for the AI assistant
const suggestedPrompts = [
  {
    title: "Financial Analysis",
    prompts: [
      "What were my top 3 expenses last month?",
      "How has my revenue changed over the past quarter?",
      "What is my profit margin for this year so far?",
      "How much did I spend on marketing in Q2?"
    ]
  },
  {
    title: "Tax Questions",
    prompts: [
      "What business expenses are tax deductible?",
      "How should I categorize my software subscription costs?",
      "What documents do I need to keep for tax purposes?",
      "How do quarterly tax payments work?"
    ]
  },
  {
    title: "Business Planning",
    prompts: [
      "Based on my current growth rate, what will my revenue look like next quarter?",
      "How does my cash flow compare to industry benchmarks?",
      "What steps should I take to improve my profitability?",
      "Should I be concerned about my current debt-to-income ratio?"
    ]
  }
];

// Chip component for suggested prompts
function SuggestedPromptChip({ text, onClick }: { text: string; onClick: () => void }) {
  return (
    <button
      className="px-3 py-1.5 bg-primary-50 text-primary-700 rounded-full text-sm font-medium hover:bg-primary-100 transition-colors"
      onClick={onClick}
    >
      {text}
    </button>
  );
}

export default function ChatPage() {
  // Ref for the chat input
  const chatInputRef = useRef<HTMLTextAreaElement>(null);
  const [chatKey, setChatKey] = useState(0);
  
  // Function to set a prompt in the chat input
  const setPrompt = (prompt: string) => {
    if (chatInputRef.current) {
      // Set the value
      chatInputRef.current.value = prompt;
      
      // Focus the input
      chatInputRef.current.focus();
      
      // Trigger input event to update state in AIChat component
      const event = new Event('input', { bubbles: true });
      chatInputRef.current.dispatchEvent(event);
    }
  };

  return (
    <Layout>
      <div className="px-4 py-6 sm:px-6 lg:px-8 flex flex-col h-[calc(100vh-4rem)]">
        {/* Page header */}
        <div className="mb-6 flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 flex items-center">
              <MessageSquare className="h-6 w-6 mr-2 text-primary" />
              ZentroBooks AI Assistant
            </h1>
            <p className="mt-1 text-neutral-500">
              Ask questions about your finances and get AI-powered insights
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              // Call the clear function exposed by AIChat component
              if ((window as any).clearChatHistory) {
                (window as any).clearChatHistory();
              }
            }}
            className="flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            Clear History
          </Button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full pb-6">
          {/* Main chat area */}
          <Card className="lg:col-span-2 flex flex-col h-full">
            <CardContent className="p-0 flex-grow flex flex-col">
              <AIChat key={chatKey} onClearHistory={() => setChatKey(prev => prev + 1)} />
            </CardContent>
          </Card>
          
          {/* Suggestions panel */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center text-lg">
                <LightbulbIcon className="h-5 w-5 mr-2 text-yellow-500" />
                Suggested Questions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {suggestedPrompts.map((category, index) => (
                  <div key={index}>
                    <h3 className="font-medium mb-3">{category.title}</h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {category.prompts.map((prompt, promptIndex) => (
                        <SuggestedPromptChip 
                          key={promptIndex} 
                          text={prompt} 
                          onClick={() => setPrompt(prompt)} 
                        />
                      ))}
                    </div>
                    {index < suggestedPrompts.length - 1 && (
                      <Separator className="mt-4" />
                    )}
                  </div>
                ))}
              </div>
              
              <div className="mt-6 p-4 bg-neutral-50 rounded-lg border border-neutral-100">
                <h3 className="font-medium mb-2 flex items-center">
                  <LightbulbIcon className="h-4 w-4 mr-2 text-yellow-500" />
                  Pro Tips
                </h3>
                <ul className="space-y-2 text-sm text-neutral-600">
                  <li>• Be specific with dates for better analysis</li>
                  <li>• Ask follow-up questions for deeper insights</li>
                  <li>• The AI has access to your transaction history</li>
                  <li>• Try comparing periods (e.g., "this month vs. last month")</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
